/**
 * SearchLinker — аналитика взаимодействия с результатами поиска
 *
 * Отправка только при:
 *  - клике по карточке результата
 *  - любом переходе по ссылке (fallback для сессии)
 *
 * Без lifecycle-событий (beforeunload, pagehide) → не блокируется браузерами
 *
 * event = Тип события (click/navigate)
 * query = Поисковый запрос
 * uidID = результата
 * index = Позиция клика
 * hover_time = Время наведения
 * view_time = Время в viewport
 * page_time = Время на странице
 * is_mobile = Тип устройства
 * ua_hash = Короткий хэш UA (8 символов, для группировки)
 *
 */

class SearchLinker {
    constructor(containerSelector = ".results__cards", config = {}) {
        this.container = document.querySelector(containerSelector);
        if (!this.container) return;

        this.config = {
            apiEndpoint: config.apiEndpoint,
            touchHoldThreshold: 100,      // мс между обновлениями при удержании
            mobileMaxWidth: 768,
            viewportThreshold: 0.5,       // 50% карточки должно быть видно
            resizeThrottleDelay: 200,
            navigationDelay: 120,         // мс задержки перед переходом (для beacon)
            debug: false,
            ...config
        };

        this.isMobile = this.detectMobile();
        this.pageLoadTime = performance.now();
        this.sessionSent = false;
        this.mobileInfoInitialized = false;

        this.cardsData = new WeakMap();
        this.touchIntervals = new WeakMap();
        this.intersectionObserver = null;
        this.resizeTimeout = null;

        this.boundHandlers = {
            mouseEnter: this.onMouseEnter.bind(this),
            mouseLeave: this.onMouseLeave.bind(this),
            cardClick: this.onCardClick.bind(this),
            anyLinkClick: this.onAnyLinkClick.bind(this),
            resize: this.onResize.bind(this),
            touchStart: this.onTouchStart.bind(this),
            touchEnd: this.onTouchEnd.bind(this)
        };

        this.init();
    }

    /* ==================== ИНИЦИАЛИЗАЦИЯ ==================== */

    init() {
        // Делегирование событий на контейнере
        this.container.addEventListener("mouseenter", this.boundHandlers.mouseEnter, true);
        this.container.addEventListener("mouseleave", this.boundHandlers.mouseLeave, true);
        this.container.addEventListener("click", this.boundHandlers.cardClick, true);

        // Fallback: любой переход по ссылке
        document.addEventListener("click", this.boundHandlers.anyLinkClick, true);

        // Throttled resize
        window.addEventListener("resize", this.boundHandlers.resize);

        // Мобильные события
        if (this.isMobile) {
            this.initMobileInfo();
        }

        // Intersection Observer для отслеживания видимости
        this.initIntersectionObserver();

        // Инициализация данных карточек
        this.updateCardsList();

        this.log('Инициализирован', { mobile: this.isMobile });
    }

    /* ==================== ПОИСКОВЫЙ ЗАПРОС ==================== */

    /**
     * Получить текущий поисковый запрос
     * Вызывается в момент отправки, т.к. URL может измениться через pushState
     */
    getSearchQuery() {
        // Сначала пробуем из URL
        const params = new URLSearchParams(window.location.search);
        const urlQuery = params.get('q');
        if (urlQuery) return urlQuery;

        // Fallback: из поля ввода
        const input = document.querySelector('[data-searcher-input]');
        if (input && input.value) return input.value.trim();

        return '';
    }

    /* ==================== КАРТОЧКИ ==================== */

    /**
     * Обновить список карточек (для динамически загруженного контента)
     */
    updateCardsList() {
        this.container.querySelectorAll(".results__card").forEach((card, index) => {
            if (this.cardsData.has(card)) return;

            this.cardsData.set(card, {
                hoverStart: null,
                viewStart: null,
                hoverTotal: 0,
                viewTotal: 0,
                index: index + 1
            });

            if (this.intersectionObserver) {
                this.intersectionObserver.observe(card);
            }
        });
    }

    /**
     * Intersection Observer для отслеживания видимости карточек
     */
    initIntersectionObserver() {
        if (!('IntersectionObserver' in window)) {
            this.warn('IntersectionObserver не поддерживается');
            return;
        }

        this.intersectionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    const data = this.cardsData.get(entry.target);
                    if (!data) return;

                    if (entry.isIntersecting) {
                        data.viewStart = performance.now();
                    } else if (data.viewStart !== null) {
                        data.viewTotal += performance.now() - data.viewStart;
                        data.viewStart = null;
                    }
                });
            },
            { threshold: this.config.viewportThreshold }
        );
    }

    /* ==================== HOVER ==================== */

    onMouseEnter(e) {
        const card = e.target.closest(".results__card");
        const data = card && this.cardsData.get(card);
        if (data) {
            data.hoverStart = performance.now();
        }
    }

    onMouseLeave(e) {
        const card = e.target.closest(".results__card");
        const data = card && this.cardsData.get(card);
        if (data && data.hoverStart !== null) {
            data.hoverTotal += performance.now() - data.hoverStart;
            data.hoverStart = null;
        }
    }

    /* ==================== КЛИК ПО КАРТОЧКЕ ==================== */

    onCardClick(e) {
        const link = e.target.closest(".results__card-title > a");
        if (!link) return;

        const card = link.closest(".results__card");
        const data = card && this.cardsData.get(card);
        if (!data) return;

        e.preventDefault();
        this.finalizeCardData(data);

        const uid = card.dataset.uid;
        const href = link.href;
        const openInNewTab = link.target === "_blank";

        if (!uid) {
            this.navigateTo(href, openInNewTab);
            return;
        }

        const payload = {
            et: 'click',                    // тип события
            q: this.getSearchQuery(),       // поисковый запрос (получаем в момент клика)
            uid,                            // ID результата
            idx: data.index,                // позиция в списке
            cht: Math.round(data.hoverTotal / 100),  // время наведения (0.1 сек)
            vt: Math.round(data.viewTotal / 100),    // время в viewport (0.1 сек)
            pgt: Math.round((performance.now() - this.pageLoadTime) / 100),  // время на странице
            plt: this.getPageLoadTime(),    // время загрузки страницы
            im: this.isMobile ? "1" : "0"   // мобильное устройство
        };

        this.sessionSent = true;
        this.send(payload);

        // Задержка для отправки beacon
        setTimeout(() => {
            this.navigateTo(href, openInNewTab);
        }, this.config.navigationDelay);
    }

    /**
     * Переход по ссылке с учётом target
     */
    navigateTo(href, openInNewTab) {
        if (openInNewTab) {
            window.open(href, '_blank', 'noopener');
        } else {
            window.location.href = href;
        }
    }

    /* ==================== ЛЮБОЙ ПЕРЕХОД (FALLBACK) ==================== */

    onAnyLinkClick(e) {
        // Если уже отправили данные — пропускаем
        if (this.sessionSent) return;

        const link = e.target.closest('a[href]');
        if (!link) return;

        // Пропускаем внешние ссылки и ссылки в новой вкладке
        if (link.target === '_blank') return;

        // Пропускаем ссылки внутри контейнера (обрабатываются в onCardClick)
        if (this.container.contains(link)) return;

        this.sessionSent = true;

        this.send({
            et: 'navigate',
            q: this.getSearchQuery(),
            pgt: Math.round((performance.now() - this.pageLoadTime) / 100),
            plt: this.getPageLoadTime(),
            im: this.isMobile ? "1" : "0"
        });

        this.log('Переход со страницы', link.href);
    }

    /* ==================== МОБИЛЬНЫЕ СОБЫТИЯ ==================== */

    initMobileInfo() {
        if (this.mobileInfoInitialized) return;

        this.container.addEventListener("touchstart", this.boundHandlers.touchStart, { passive: true });
        this.container.addEventListener("touchend", this.boundHandlers.touchEnd);
        this.container.addEventListener("touchcancel", this.boundHandlers.touchEnd);

        this.mobileInfoInitialized = true;
        this.log('Мобильный учет включен');
    }

    removeMobileInfo() {
        if (!this.mobileInfoInitialized) return;

        this.container.removeEventListener("touchstart", this.boundHandlers.touchStart);
        this.container.removeEventListener("touchend", this.boundHandlers.touchEnd);
        this.container.removeEventListener("touchcancel", this.boundHandlers.touchEnd);

        // Очищаем активные интервалы
        this.container.querySelectorAll(".results__card").forEach(card => {
            const info = this.touchIntervals.get(card);
            if (info) {
                clearInterval(info.interval);
                this.touchIntervals.delete(card);
            }
        });

        this.mobileInfoInitialized = false;
        this.log('Мобильный учет отключен');
    }

    onTouchStart(e) {
        const card = e.target.closest(".results__card");
        const data = card && this.cardsData.get(card);
        if (!data) return;

        const startTime = performance.now();
        const interval = setInterval(() => {
            data.hoverTotal += this.config.touchHoldThreshold;
        }, this.config.touchHoldThreshold);

        this.touchIntervals.set(card, { interval, startTime });
    }

    onTouchEnd(e) {
        const card = e.target.closest(".results__card");
        const info = card && this.touchIntervals.get(card);
        if (!info) return;

        clearInterval(info.interval);

        // Добавляем остаток времени
        const elapsed = performance.now() - info.startTime;
        const remainder = elapsed % this.config.touchHoldThreshold;
        const data = this.cardsData.get(card);
        if (data) {
            data.hoverTotal += remainder;
        }

        this.touchIntervals.delete(card);
    }

    /* ==================== ВСПОМОГАТЕЛЬНЫЕ МЕТОДЫ ==================== */

    /**
     * Финализация данных карточки перед отправкой
     */
    finalizeCardData(data) {
        const now = performance.now();

        if (data.hoverStart !== null) {
            data.hoverTotal += now - data.hoverStart;
            data.hoverStart = null;
        }

        if (data.viewStart !== null) {
            data.viewTotal += now - data.viewStart;
            data.viewStart = null;
        }
    }

    /**
     * Время загрузки страницы (Performance API)
     */
    getPageLoadTime() {
        // Используем современный Navigation Timing API
        const navigation = performance.getEntriesByType('navigation')[0];
        if (navigation) {
            return Math.round(navigation.domContentLoadedEventEnd / 100);
        }

        // Fallback для старых браузеров
        if (performance.timing) {
            return Math.round(performance.timing.domContentLoadedEventEnd / 100);
        }

        return 0;
    }

    /**
     * Определение мобильного устройства
     */
    detectMobile() {
        return /Mobi|Android|iPhone/i.test(navigator.userAgent)
            || window.innerWidth < this.config.mobileMaxWidth;
    }

    /**
     * Обработчик resize с throttle
     */
    onResize() {
        if (this.resizeTimeout) return;

        this.resizeTimeout = setTimeout(() => {
            const wasMobile = this.isMobile;
            this.isMobile = this.detectMobile();

            // Включаем/выключаем мобильное отслеживание
            if (!wasMobile && this.isMobile) {
                this.initMobileInfo();
            } else if (wasMobile && !this.isMobile) {
                this.removeMobileInfo();
            }

            this.resizeTimeout = null;
        }, this.config.resizeThrottleDelay);
    }

    /* ==================== ОТПРАВКА ==================== */

    /**
     * Отправка данных через Beacon API или fetch
     */
    send(payload) {
        const body = JSON.stringify({
            ...payload,
            ts: Date.now()  // timestamp
        });

        if (navigator.sendBeacon) {
            const sent = navigator.sendBeacon(this.config.apiEndpoint, body);
            this.log('Beacon отправлен:', sent, payload);
        } else {
            // Fallback для старых браузеров
            fetch(this.config.apiEndpoint, {
                method: 'POST',
                body,
                headers: { 'Content-Type': 'application/json' },
                keepalive: true
            }).catch(err => this.error('Ошибка отправки:', err));

            this.log('Fetch отправлен:', payload);
        }
    }

    /* ==================== ОЧИСТКА ==================== */

    /**
     * Полная очистка (для SPA)
     */
    destroy() {
        if (!this.container) return;

        // Удаляем слушатели контейнера
        this.container.removeEventListener("mouseenter", this.boundHandlers.mouseEnter, true);
        this.container.removeEventListener("mouseleave", this.boundHandlers.mouseLeave, true);
        this.container.removeEventListener("click", this.boundHandlers.cardClick, true);

        // Удаляем глобальные слушатели
        document.removeEventListener("click", this.boundHandlers.anyLinkClick, true);
        window.removeEventListener("resize", this.boundHandlers.resize);

        // Очищаем таймер resize
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = null;
        }

        // Очищаем мобильные слушатели
        this.removeMobileInfo();

        // Отключаем Intersection Observer
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
            this.intersectionObserver = null;
        }

        this.log('Destroyed');
    }

    /* ==================== ЛОГИРОВАНИЕ ==================== */

    log(...args) {
        if (this.config.debug) {
            console.log('[SearchLinker]', ...args);
        }
    }

    warn(...args) {
        if (this.config.debug) {
            console.warn('[SearchLinker]', ...args);
        }
    }

    error(...args) {
        if (this.config.debug) {
            console.error('[SearchLinker]', ...args);
        }
    }
}

export default SearchLinker;